import React, { useEffect } from "react";
import { Button, CustomSelect, Input, CustomPhoneInput, OptionType } from "components";
import { PersonalInfoData } from "./types";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { howOptions } from "features/registration/onboarding/utils";

const roleOptions = [
  { label: "Founder/CEO", value: "founder_ceo" },
  { label: "C-level/Director/HOD", value: "c_level" },
  { label: "Company HR/Recruiter", value: "company_hr" },
  { label: "HR/Talent Agency", value: "hr_agency" },
  { label: "Independent Consultant", value: "independent_consultant" },
  { label: "Other", value: "other" },
];

// Updated schema to match the PersonalInfoData type structure
const personalSchema = yup.object().shape({
  firstName: yup
    .string()
    .required("Required")
    .matches(/^[A-Za-z\s]+$/, "First name should only contain alphabets"),
  lastName: yup
    .string()
    .required("Required")
    .matches(/^[A-Za-z\s]+$/, "Last name should only contain alphabets"),
  role: yup.string().required("Required"),
  number: yup.object().shape({
    code: yup.string().required("Required"),
    number: yup
      .string()
      .required("Required")
      .matches(/^[0-9]+$/, "Phone number must contain only digits")
      .min(8, "Phone number must be at least 8 digits")
      .max(15, "Phone number must not exceed 15 digits")
      .test('nigerian-length', 'Nigerian phone number must be exactly 10 digits', function(value) {
        const { code } = this.parent;
        // For Nigerian numbers (+234), enforce exactly 10 digits
        if (code === '+234' && value) {
          return value.length === 10;
        }
        return true;
      }),
    value: yup.string().required("Required"),
  }).required("Required"),
  how: yup.object().shape({
    label: yup.string().required("Required"),
    value: yup.string().required("Required"),
  }).required("Required"),
  country: yup.object().shape({
    label: yup.string().required("Required"),
    value: yup.string().required("Required"),
  }).required("Required"),
  other: yup.string().when("how", {
    is: (val: any) => val?.value === "others" || val?.value === "referral",
    then: (schema) => schema.required("Required"),
    otherwise: (schema) => schema.optional(),
  }),
});

interface PersonalInfoProps {
  submit: (data: PersonalInfoData) => void;
  init: PersonalInfoData;
  countryOptions: OptionType[];
  isSubmitting?: boolean;
}

export const PersonalInfoForm: React.FC<PersonalInfoProps> = ({
  submit,
  init,
  countryOptions,
  isSubmitting,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm<PersonalInfoData>({
    resolver: yupResolver(personalSchema) as any,
    mode: "onTouched",
    defaultValues: init,
  });

  // Reset form values when init changes (e.g., when navigating back)
  useEffect(() => {
    if (init) {
      reset(init);
    }
  }, [init, reset]);

  // Properly typed submit handler
  const onSubmit = (data: PersonalInfoData) => {
    submit(data);
  };

  // Handler to allow only alphabets in name fields
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const value = input.value;
    // Remove any non-alphabetic characters except spaces
    const filteredValue = value.replace(/[^A-Za-z\s]/g, '');
    // Update the form value with filtered value
    setValue(input.name as 'firstName' | 'lastName', filteredValue);
  };

  return (
    <form className="max-h-[380px] overflow-y-auto scrollbar-none">
      <div className="mb-6">
        <label className="text-sm bg-transparent p-0 text-gray-600 mb-2.5 font-medium block">
          Which best describes you <span className="text-red-500">*</span>
        </label>
        <CustomSelect
          placeholder="Select your role"
          options={roleOptions}
          onChange={(val) => setValue("role", val.value)}
          validatorMessage={errors.role?.message?.toString() ?? ""}
          name="role"
          value={roleOptions.find(opt => opt.value === watch("role")) || { label: "", value: "" }}
          showRequired
        />
      </div>

      <div className="mb-6 space-y-4">
        <label className="text-sm bg-transparent p-0 text-gray-600 mb-2.5 font-medium block">
          First Name <span className="text-red-500">*</span>
        </label>
        <Input
          placeholder="Enter your first name"
          type="text"
          required
          validatorMessage={errors.firstName?.message}
          name="firstName"
          register={register}
          value={watch("firstName")}
          showRequired
          onChange={handleNameChange}
        />
        <label className="text-sm bg-transparent p-0 text-gray-600 mb-2.5 font-medium block">
          Last Name <span className="text-red-500">*</span>
        </label>
        <Input
          placeholder="Enter your last name"
          type="text"
          required
          validatorMessage={errors.lastName?.message}
          name="lastName"
          register={register}
          value={watch("lastName")}
          showRequired
          onChange={handleNameChange}
        />
      </div>

      <div className="mb-6">
        <label className="text-sm bg-transparent p-0 text-gray-600 mb-2.5 font-medium block">
          Phone Number <span className="text-red-500">*</span>
        </label>
        <CustomPhoneInput
          placeholder="900000000000"
          validatorMessage={errors.number?.number?.message?.toString() || errors.number?.code?.message?.toString() || errors.number?.value?.message?.toString() || ""}
          onChange={({ code, number, value }) =>
            setValue("number", { code, number, value })
          }
          showRequired
          value={watch("number")?.value || ""}
        />
      </div>

      <CustomSelect
        menuPlacement="top"
        label="Country"
        placeholder="Select country"
        options={countryOptions}
        onChange={(val) => setValue("country", val)}
        validatorMessage={errors.country?.value?.message?.toString() ?? ""}
        name="country"
        value={watch("country") || { label: "", value: "" }}
        showRequired
      />

      <div className="mb-6">
        <label className="text-sm bg-transparent p-0 text-gray-600 mb-2.5 font-medium block">
          How Did You Know About Us <span className="text-red-500">*</span>
        </label>
        <CustomSelect
          menuPlacement="top"
          placeholder="Select option"
          options={howOptions}
          onChange={(val) => setValue("how", val)}
          validatorMessage={errors.how?.value?.message?.toString() ?? ""}
          name="how"
          value={watch("how") || { label: "", value: "" }}
          showRequired
        />
      </div>

      {(watch("how")?.value === "others" || watch("how")?.value === "referral") && (
        <Input
          label={`How did you hear about us? (${watch("how")?.label})`}
          placeholder="How did you hear about us"
          type="text"
          required
          validatorMessage={errors.other?.message}
          name="other"
          register={register}
          value={watch("other") || ""}
          showRequired
        />
      )}

      <Button
        className="ml-auto mr-auto mt-8 w-full !bg-[#012169] !hover:bg-[#012069cd] !border-none text-white"
        type="fill"
        onClick={handleSubmit(onSubmit)}
      >
        {isSubmitting ? "Submitting..." : "Next"}
      </Button>
    </form>
  );
};